import React, { useState } from 'react';
import { mockData } from '../data/mockData';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessType: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast.success('Thank you! We\'ll contact you within 24 hours.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      businessType: '',
      message: '',
    });
  };

  const handleWhatsApp = () => {
    toast.info('WhatsApp contact will be available soon. Please use email for now.');
  };

  return (
    <section id="contact" className="relative py-32 bg-black">
      <div className="max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="display-large text-white mb-6">
            Get in <span className="text-[#00FFD1]">Touch</span>
          </h2>
          <p className="body-large text-white/85 max-w-3xl mx-auto">
            Ready to showcase your business with a stunning 360° virtual tour? 
            Contact us today for a consultation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="display-medium text-white mb-8">
                Let's Create Something Amazing
              </h3>
              <p className="body-medium text-white/70 mb-8">
                Whether you're a restaurant owner, hotelier, real estate agent, or run any business, 
                we're here to help you stand out with immersive virtual tours.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start gap-4 p-6 bg-white/5 backdrop-blur-sm border border-white/10 hover:border-[#00FFD1]/50 transition-all duration-400">
                <div className="w-12 h-12 bg-[#00FFD1]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#00FFD1]" />
                </div>
                <div>
                  <h4 className="heading-3 text-white mb-2">Email</h4>
                  <a 
                    href={`mailto:${mockData.company.email}`}
                    className="body-medium text-white/70 hover:text-[#00FFD1] transition-colors"
                  >
                    {mockData.company.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-white/5 backdrop-blur-sm border border-white/10 hover:border-[#00FFD1]/50 transition-all duration-400">
                <div className="w-12 h-12 bg-[#00FFD1]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#00FFD1]" />
                </div>
                <div>
                  <h4 className="heading-3 text-white mb-2">Phone</h4>
                  <p className="body-medium text-white/70">
                    {mockData.company.phone}
                    <br />
                    <span className="text-sm text-white/50">(Contact details coming soon)</span>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-white/5 backdrop-blur-sm border border-white/10 hover:border-[#00FFD1]/50 transition-all duration-400">
                <div className="w-12 h-12 bg-[#00FFD1]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#00FFD1]" />
                </div>
                <div>
                  <h4 className="heading-3 text-white mb-2">Location</h4>
                  <p className="body-medium text-white/70">
                    {mockData.company.location}
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp Button */}
            <Button 
              onClick={handleWhatsApp}
              className="btn-primary w-full"
            >
              <MessageCircle className="w-5 h-5" />
              Contact via WhatsApp
            </Button>
          </div>

          {/* Contact Form */}
          <div className="p-8 bg-white/5 backdrop-blur-sm border border-white/10">
            <h3 className="heading-2 text-white mb-6">Send us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-white/70 mb-2 body-small">Name *</label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                  className="w-full bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#00FFD1] h-12"
                />
              </div>

              <div>
                <label className="block text-white/70 mb-2 body-small">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  className="w-full bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#00FFD1] h-12"
                />
              </div>

              <div>
                <label className="block text-white/70 mb-2 body-small">Phone</label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX"
                  className="w-full bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#00FFD1] h-12"
                />
              </div>

              <div>
                <label className="block text-white/70 mb-2 body-small">Business Type</label>
                <Input
                  type="text"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                  placeholder="e.g., Restaurant, Hotel, Real Estate"
                  className="w-full bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#00FFD1] h-12"
                />
              </div>

              <div>
                <label className="block text-white/70 mb-2 body-small">Message *</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Tell us about your project..."
                  rows={4}
                  className="w-full bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#00FFD1] resize-none"
                />
              </div>

              <Button type="submit" className="btn-primary w-full">
                Send Message
                <Send className="w-5 h-5" />
              </Button>
            </form>
          </div>
        </div>

        {/* Map Placeholder */}
        <div className="mt-16 h-96 bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-[#00FFD1] mx-auto mb-4" />
            <p className="text-white/70 body-medium">
              Interactive Google Maps integration coming soon
            </p>
            <p className="text-white/50 body-small mt-2">
              Serving Manipal, Udupi & across Karnataka
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
